import React, { useState, useEffect, useMemo } from "react";
import { Badge, Col, Form, Image, Modal, Row, Table, Nav } from "react-bootstrap";
import { oneKlicks } from '../../SDK/oneclick';
import ImageShow from '../../Uitiles/ImageShow';
import { toast } from 'react-toastify';
import AddEmployeeModal from "./Components/AddEmployeeModal";
import UserActivityTracking from "./UserActivityTracking";
import ResponsiveSearchBar from "./Components/ResponsiveSearchBar";
import { CardView, Flex, Grid, RowView, ColView, Heading, Paragraph, Button, Alert, Spinner } from '../../layouts/MainCompontes/index';

const EmployeeManagement = () => {
    const [search, setSearch] = useState("");
    const [openAdd, setOpenAdd] = useState(false);
    const [employeeList, setEmployeeList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [showActivityTracking, setShowActivityTracking] = useState(false);

    useEffect(() => {
        fetchEmployeeList();
    }, []);

    const fetchEmployeeList = async () => {
        setLoading(true);
        try {
            const tableSchema = '';
            const tableName = 'employees';
            const conditions = {};
            const orderBy = 'id DESC';
            const search = {};
            const pagination = {};
            const margedata = [
                {
                    target_table: 'users',
                    target_column: 'id',
                    target_value: 'staff_id',
                    target_label: 'staff'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);

            if (response.success) {
                setEmployeeList(response.data);
            } else {
                toast.error('Failed to fetch employee list');
            }
        } catch (error) {
            console.error('Error fetching employee list:', error);
            toast.error('Error fetching employee list');
        } finally {
            setLoading(false);
        }
    };

    const handleAddEmployee = () => {
        setEditingEmployee(null);
        setOpenAdd(true);
    };

    const handleEditEmployee = (employee) => {
        setEditingEmployee(employee);
        setOpenAdd(true);
    };

    const handleDeleteEmployee = async (employeeId) => {
        if (window.confirm('Are you sure you want to delete this employee record?')) {
            try {
                const tableSchema = '';
                const tableName = 'employees';
                const conditions = { id: employeeId };

                const response = await oneKlicks.delete(tableSchema, tableName, conditions);

                if (response.success) {
                    toast.success('Employee record deleted successfully');
                    fetchEmployeeList();
                } else {
                    toast.error('Failed to delete employee record');
                }
            } catch (error) {
                console.error('Error deleting employee:', error);
                toast.error('Error deleting employee record');
            }
        }
    };

    const handleEmployeeSuccess = () => {
        fetchEmployeeList();
        setOpenAdd(false);
    };

    const handleViewActivity = () => {
        setShowActivityTracking(true);
    };

    const filteredEmployees = useMemo(() => {
        if (!search.trim()) return employeeList;

        return employeeList.filter(employee => {
            const staffName = `${employee.staff?.[0]?.first_name || ''} ${employee.staff?.[0]?.last_name || ''}`.toLowerCase();
            const employeeId = employee.employee_id?.toLowerCase() || '';
            const department = employee.department?.toLowerCase() || '';
            const designation = employee.designation?.toLowerCase() || '';
            const status = employee.status?.toLowerCase() || '';

            const searchTerm = search.toLowerCase();

            return staffName.includes(searchTerm) ||
                employeeId.includes(searchTerm) ||
                department.includes(searchTerm) ||
                designation.includes(searchTerm) ||
                status.includes(searchTerm);
        });
    }, [employeeList, search]);

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { variant: 'success', text: 'Active' },
            inactive: { variant: 'secondary', text: 'Inactive' },
            on_leave: { variant: 'warning', text: 'On Leave' },
            terminated: { variant: 'danger', text: 'Terminated' }
        };

        const config = statusConfig[status] || statusConfig.active;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getEmploymentTypeBadge = (type) => {
        const typeConfig = {
            full_time: { variant: 'primary', text: 'Full Time' },
            part_time: { variant: 'info', text: 'Part Time' },
            contract: { variant: 'warning', text: 'Contract' },
            intern: { variant: 'success', text: 'Intern' },
            consultant: { variant: 'dark', text: 'Consultant' }
        };

        const config = typeConfig[type] || typeConfig.full_time;
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN');
    };

    return (
        <div className="dc-haslayout dc-dbsectionspace">
            <div className="container-fluid py-3">
                <CardView>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h5 className="mb-0 font-weight-bold mr-3">Employee Management</h5>
                        <Badge bg="info">HRM System</Badge>
                    </div>
                    <ResponsiveSearchBar
                        searchValue={search}
                        onSearchChange={(e) => setSearch(e.target.value)}
                        searchPlaceholder="Search employees..."
                        actions={[
                            {
                                variant: "success",
                                onClick: handleViewActivity,
                                icon: <i className="fas fa-chart-line"></i>,
                                text: "View Activity Tracking",
                                mobileText: "Activity Tracking"
                            },
                            {
                                variant: "primary",
                                onClick: handleAddEmployee,
                                icon: "+",
                                text: "Add Employee"
                            }
                        ]}
                    />
                </CardView>

                {/* Summary Cards */}
                <Row className="mb-4">
                    <Col md={3}>
                        <div className="card text-center">
                            <div className="card-body">
                                <h3 className="text-primary">{employeeList.length}</h3>
                                <p className="mb-0">Total Employees</p>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="card text-center">
                            <div className="card-body">
                                <h3 className="text-success">
                                    {employeeList.filter(emp => emp.status === 'active').length}
                                </h3>
                                <p className="mb-0">Active Employees</p>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="card text-center">
                            <div className="card-body">
                                <h3 className="text-warning">
                                    {employeeList.filter(emp => emp.status === 'on_leave').length}
                                </h3>
                                <p className="mb-0">On Leave</p>
                            </div>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className="card text-center">
                            <div className="card-body">
                                <h3 className="text-info">
                                    {employeeList.reduce((sum, emp) => sum + (parseFloat(emp.net_salary) || 0), 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 })}
                                </h3>
                                <p className="mb-0">Total Payroll</p>
                            </div>
                        </div>
                    </Col>
                </Row>

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" />
                                <p className="mt-2">Loading employee records...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr>
                                            <th>Employee ID</th>
                                            <th>Employee</th>
                                            <th>Department</th>
                                            <th>Designation</th>
                                            <th>Salary</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Joining Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEmployees.length > 0 ? (
                                            filteredEmployees.map((employee, index) => (
                                                <tr key={employee.id}>
                                                    <td data-label="Employee ID">
                                                        <span className="fw-bold text-primary">
                                                            {employee.employee_id}
                                                        </span>
                                                    </td>
                                                    <td data-label="Employee">
                                                        <div className="d-flex align-items-center">
                                                            <ImageShow
                                                                url={employee.staff?.[0]?.profile_photo_url}
                                                                width={40}
                                                                height={40}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '50%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                            <div width="100%" style={{ marginLeft: 10 }}>
                                                                <h6 className="fw-bold text-truncate" style={{ maxWidth: 150, height: '23px' }}>
                                                                    {(employee.staff?.[0]?.first_name + ' ' + employee.staff?.[0]?.last_name)?.length > 18
                                                                        ? (employee.staff?.[0]?.first_name + ' ' + employee.staff?.[0]?.last_name).slice(0, 15) + '...'
                                                                        : (employee.staff?.[0]?.first_name + ' ' + employee.staff?.[0]?.last_name)}
                                                                    <br />
                                                                    {employee.staff?.[0]?.email?.length > 16
                                                                        ? (employee.staff?.[0]?.email).slice(0, 13) + '...'
                                                                        : employee.staff?.[0]?.email}
                                                                </h6>
                                                                <span className="text-muted text-truncate" style={{ fontSize: 12, maxWidth: 150, display: 'inline-block', verticalAlign: 'bottom' }}>
                                                                    {employee.staff?.[0]?.email?.length > 16
                                                                        ? (employee.staff?.[0]?.email).slice(0, 13) + '...'
                                                                        : employee.staff?.[0]?.email}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Department">
                                                        <span className="badge bg-light text-dark">
                                                            {employee.department}
                                                        </span>
                                                    </td>
                                                    <td data-label="Designation">
                                                        <span className="fw-bold">
                                                            {employee.designation}
                                                        </span>
                                                    </td>
                                                    <td data-label="Salary">
                                                        <div>
                                                            <div className="fw-bold text-success">
                                                                {formatCurrency(employee.net_salary)}
                                                            </div>
                                                            <small className="text-muted">
                                                                Base: {formatCurrency(employee.base_salary)}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td data-label="Type">{getEmploymentTypeBadge(employee.employment_type)}</td>
                                                    <td data-label="Status">{getStatusBadge(employee.status)}</td>
                                                    <td data-label="Joining Date">
                                                        <span className="text-muted">
                                                            {formatDate(employee.joining_date)}
                                                        </span>
                                                    </td>
                                                    <td data-label="Actions">
                                                        <div className="d-flex gap-1">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleEditEmployee(employee)}
                                                                title="Edit Employee"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                title="View Details"
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteEmployee(employee.id)}
                                                                title="Delete Employee"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4">
                                                    <Alert variant="info">
                                                        No employee records found.
                                                        {search && ' Try adjusting your search criteria.'}
                                                        {!search && ' Click "Add Employee" to get started.'}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <AddEmployeeModal
                show={openAdd}
                onHide={() => setOpenAdd(false)}
                onSuccess={handleEmployeeSuccess}
                editingEmployee={editingEmployee}
            />

            {/* Activity Tracking Modal */}
            <Modal show={showActivityTracking} onHide={() => setShowActivityTracking(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>User Activity Tracking</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: 0 }}>
                    <UserActivityTracking />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowActivityTracking(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EmployeeManagement;
